import { Navigate, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useLoginForm } from '../features/auth/model/use-login-form';
import { LoginConsents } from '../features/auth/ui/login-consents';
import { LoginHeader } from '../features/auth/ui/login-header';
import { LoginPhoneEntry } from '../features/auth/ui/login-phone-entry';
import { LoginSubmitButton } from '../features/auth/ui/login-submit-button';
import { useResolvedAuthenticatedPath } from '../shared/hooks/use-employee-session';
import { ViewportFrame, ViewportPage } from '../shared/ui';

const LoginContent = styled.div.attrs({
  'data-ui': 'login-page',
})`
  height: 100%;
  display: flex;
  flex-direction: column;

  & > [data-ui='auth-login-header'] {
    margin-bottom: ${({ theme }) => theme.scale.px(25)};
  }

  & > [data-ui='auth-login-phone-entry'] {
    margin-bottom: ${({ theme }) => theme.scale.px(36)};
  }

  /* Parent-owned rhythm: submit button keeps zero external margins in its own file. */
  & > [data-ui='auth-login-submit-button'] {
    margin: 0;
  }

  & > [data-ui='auth-login-consents'] {
    margin-top: auto;
  }
`;

const SubmitError = styled.p`
  margin: ${({ theme }) => `${theme.scale.px(8)} 0 0`};
  align-self: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1;
  color: ${({ theme }) => theme.colors.support.danger};
`;

export function LoginPage() {
  const redirectPath = useResolvedAuthenticatedPath();
  const navigate = useNavigate();
  const { consents, phoneForm, isSubmitting, submitError, submit } =
    useLoginForm();

  if (redirectPath === null) {
    return null;
  }

  if (redirectPath !== '/login') {
    return <Navigate replace to={redirectPath} />;
  }

  const handleSubmit = async () => {
    const result = await submit();
    if (result !== null) {
      navigate('/verify', {
        state: { retryAfter: result.retryAfter, expiresIn: result.expiresIn },
      });
    }
  };

  return (
    <ViewportPage dataUi="auth-viewport-page">
      <ViewportFrame
        dataUi="auth-viewport-frame"
        paddingBottomPx={28}
        paddingLeftPx={31}
        paddingRightPx={30}
        paddingTopPx={25}
      >
        <LoginContent>
          <LoginHeader />
          <LoginPhoneEntry phoneForm={phoneForm} />
          <LoginSubmitButton
            canSubmit={phoneForm.canSubmit && !isSubmitting}
            onSubmit={handleSubmit}
          />
          {submitError !== null && <SubmitError>{submitError}</SubmitError>}
          <LoginConsents consents={consents} />
        </LoginContent>
      </ViewportFrame>
    </ViewportPage>
  );
}
